import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * นาฬิกาสอบ — นับถอยหลังจาก deadline ของ server เท่านั้น
 * ห้ามนับจาก durationMinutes ตรงๆ เพราะ refresh แล้วเวลาจะ reset
 * แก้นาฬิกาเครื่องก็ไม่มีผล เพราะเราเก็บ offset เทียบกับเวลา server
 */
export type ExamTimer = {
  /** null = ไม่จับเวลา */
  remainingSeconds: number | null;
  isExpired: boolean;
  /** เรียกเมื่อได้ serverTime ใหม่จาก response ของ auto-save เพื่อ re-sync */
  syncWithServer: (serverTime: string) => void;
};

export function useExamTimer(
  deadlineAt: string | null,
  serverTime: string,
  onExpire?: () => void,
): ExamTimer {
  const offsetRef = useRef(new Date(serverTime).getTime() - Date.now());
  const expiredNotified = useRef(false);

  const computeRemaining = useCallback((): number | null => {
    if (!deadlineAt) return null;
    const estimatedServerNow = Date.now() + offsetRef.current;
    return Math.max(0, Math.round((new Date(deadlineAt).getTime() - estimatedServerNow) / 1000));
  }, [deadlineAt]);

  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(computeRemaining);

  const syncWithServer = useCallback(
    (nextServerTime: string) => {
      offsetRef.current = new Date(nextServerTime).getTime() - Date.now();
      setRemainingSeconds(computeRemaining());
    },
    [computeRemaining],
  );

  useEffect(() => {
    if (!deadlineAt) {
      setRemainingSeconds(null);
      return;
    }

    const tick = (): void => {
      const next = computeRemaining();
      setRemainingSeconds(next);
      if (next === 0 && !expiredNotified.current) {
        expiredNotified.current = true;
        onExpire?.();
      }
    };

    tick();
    const interval = window.setInterval(tick, 1_000);

    // มือถือพักหน้าจอ/สลับแอป → กลับมาต้องคำนวณใหม่ทันที ไม่รอ tick ถัดไป
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [deadlineAt, computeRemaining, onExpire]);

  return {
    remainingSeconds,
    isExpired: remainingSeconds !== null && remainingSeconds <= 0,
    syncWithServer,
  };
}
